import { Order } from '../../services/exports/Interfaces';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useTranslation } from 'react-i18next';
import Tooltip from '../Tooltip';
import InfoIcon from '../../assets/logo/InfoIcon';
import useTheme from '../../hooks/ui/useTheme';
import React, { ReactElement, useMemo } from 'react';
import CustomLoadingIndicator from '../loading/CustomLoadingIndicator';
import PaymentMethodBrandIcons from '../payment/payment-methods/PaymentMethodBrandIcons';
import useOffersTranslations from '../../features/offers/hooks/useOffersTranslations';
import PromoOrGiftCardRow from '../../features/gift-cards/components/PromoOrGiftCardRow';
import CashbackPointsRow from '../checkout/CashbackPointsRow';

interface Props {
  order: Order;
  loading?: boolean;
  className?: string;
  tipsRender?: () => ReactElement;
  withSeparatedTotal?: boolean;
  withPaymentMethod?: boolean;
  withEarnedPoints?: boolean;
  withCashbackPointsRow?: boolean;
  withPromoOrGiftCardRow?: boolean;
  /**
   * `overview` is the order-overview step, which deliberately carries NO price: no subtotal, no
   * platform fees, no total. The final price lives on the payment page, and the CTA there is the
   * only place the customer is shown what they will be charged.
   *
   * `full` (the default) renders everything and is what the payment page and order details use.
   */
  variant?: 'full' | 'overview';
}

const OrderPaymentBreakdown = ({
  order,
  loading,
  className,
  tipsRender,
  withSeparatedTotal,
  withPaymentMethod,
  withEarnedPoints,
  withCashbackPointsRow,
  withPromoOrGiftCardRow,
  variant = 'full',
}: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Order:OrderPaymentBreakdown' });

  const theme = useTheme();
  const { formatCurrency } = useAppMethods();
  const earnedPoints = useMemo(() => {
    if (!withEarnedPoints) {
      return;
    }

    return order?.cashback_account_operations?.reduce(
      (previousValue, currentValue) => previousValue + currentValue.value,
      0,
    );
  }, [withEarnedPoints, order?.cashback_account_operations]);

  const showsPrice = variant === 'full';

  const renderTips = () => {
    if (tipsRender) {
      return tipsRender();
    }

    return order.tip > 0 && <OrderPaymentBreakdown.Row name={t('tip')} amount={order.tip} />;
  };

  return (
    <div className={classnames('text-sm space-y-xs', className)}>
      {showsPrice && <OrderPaymentBreakdown.Row name={t('subtotal')} amount={order.sub_total} />}
      {order.method_fee > 0 && <OrderPaymentBreakdown.Row name={t('method_fee')} amount={order.method_fee} />}
      {showsPrice && order.service_fee > 0 && (
        <OrderPaymentBreakdown.Row
          name={
            <>
              <Tooltip title={t(`service_fee.description`)}>
                <span className="flex items-center gap-xs">
                  {t('service_fee.title')} <InfoIcon color={theme.color.gray['300']} className="w-[18px] h-[18px]" />
                </span>
              </Tooltip>
            </>
          }
          amount={order.service_fee}
          className="relative"
        />
      )}
      {showsPrice && order.payment_method_upcharge_fee > 0 && (
        <OrderPaymentBreakdown.Row
          name={
            <>
              <Tooltip title={t(`payment_method_upcharge_fee.description`)}>
                <span className="flex items-center gap-xs">
                  {t('payment_method_upcharge_fee.title')}{' '}
                  <InfoIcon color={theme.color.gray['300']} className="w-[18px] h-[18px]" />
                </span>
              </Tooltip>
            </>
          }
          amount={order.payment_method_upcharge_fee}
          className="relative"
        />
      )}
      {order.additional_tax > 0 && <OrderPaymentBreakdown.Row name={t('tax')} amount={order.additional_tax} />}
      {withPromoOrGiftCardRow && <PromoOrGiftCardRow />}
      {withCashbackPointsRow && <CashbackPointsRow />}
      {renderTips()}
      {(order.total_discount > 0 || Boolean(order.coupon_type)) && (
        <>
          <hr className="my-md" />
          {/* The numeric discount line only when there's an actual € discount (e.g. a promo below
              its minimum shows the applied label below without a "-€0" line). */}
          {order.total_discount > 0 && (
            <OrderPaymentBreakdown.Row
              name={t('discount')}
              amount={-order.total_discount}
              className="text-muted-foreground"
            />
          )}
          {/* Applied discounts are always labelled here (offer, loyalty points, promo/gift code),
              so an applied promo code shows the same way as an offer or points redemption. */}
          <AppliedOffersRow order={order} />
          <AppliedCouponRow order={order} />
          {/* The one-per-order note is intentionally NOT shown here — it already lives in the
              promo/gift + offer modals, and repeating it on checkout is redundant (CTO 2026-08-03). */}
          <hr className="my-md" />
        </>
      )}
      {showsPrice && (
        <div
          className={classnames(
            'flex justify-between gap-lg items-start',
            withSeparatedTotal && 'border-dashed border-t border-t-mobile-secondary pt-md',
          )}
        >
          <span className="flex items-center font-bold">{t('total')}</span>
          <span>
            <span className={classnames('flex items-center space-x-md place-content-end', { hidden: loading })}>
              {withPaymentMethod && order.payment && (
                <PaymentMethodBrandIcons
                  payment_method_id={order.payment.payment_method_id}
                  payment_method_brand={order.payment.payment_method_brand}
                  stripe_payment_method_type={order.payment.stripe_payment_method_type}
                />
              )}
              <span className="font-bold">{formatCurrency(order.total)}</span>
            </span>
            {!!earnedPoints && (
              <span className="place-content-end flex text-brand-primary font-medium text-sm">
                {t('earned_points', { points: earnedPoints })}
              </span>
            )}
            <CustomLoadingIndicator className={classnames('w-12', { hidden: !loading })} />
          </span>
        </div>
      )}
      {/*
        Machine-readable order totals for e2e — currency-agnostic and locale-agnostic. The visible
        rows render via formatCurrency, so scraping them would tie every assertion to the active
        locale's decimal separator and currency placement. Rendered unconditionally (unlike the rows
        above, several of which are gated on `> 0`) so a spec can assert a fee is ABSENT as well as
        present. Present on /checkout, /payment and /order-details.

        MUST STAY THE LAST CHILD. The parent is `space-y-xs`, which Tailwind compiles to
        `& > * + * { margin-top }` — a DOM-adjacency rule, not a layout one, so `hidden`
        (display:none) removes the box but NOT the sibling relationship. As the first child it gave
        the subtotal row an 8px top margin on all three screens. Last, the rule applies to the
        marker itself, which has no box, and nothing moves.
        (order-basket/index.tsx has the same marker as a non-last child inside space-y-md — same
        16px bug, pre-existing, worth a follow-up.)
      */}
      <span
        data-test="order-totals"
        data-subtotal={order.sub_total}
        data-method-fee={order.method_fee}
        data-service-fee={order.service_fee}
        data-upcharge-fee={order.payment_method_upcharge_fee}
        data-tax={order.additional_tax}
        data-tip={order.tip}
        data-discount={order.total_discount}
        data-total={order.total}
        data-total-shown={showsPrice ? 'true' : 'false'}
        data-online-payment-total={order.online_payment_total}
        data-loading={loading ? 'true' : 'false'}
        className="hidden"
      />
    </div>
  );
};

export default OrderPaymentBreakdown;

type AppliedRowProps = {
  order: Order;
};

const AppliedOffersRow = ({ order }: AppliedRowProps) => {
  const { getLabelByOffer } = useOffersTranslations();
  const appliedOffers = order.applied_offers;

  if (!appliedOffers?.length) {
    return;
  }

  return (
    <>
      {appliedOffers.map((appliedOffer) => (
        <p className="flex justify-between items-center text-muted-foreground text-sm" key={appliedOffer.id}>
          {getLabelByOffer(appliedOffer)}
        </p>
      ))}
    </>
  );
};

const AppliedCouponRow = ({ order }: AppliedRowProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Shared:Coupons' });

  if (!order.coupon_type) {
    return;
  }

  return <p className="text-muted-foreground text-sm">{t(order.coupon_type, { code: order.coupon_details?.code })}</p>;
};

OrderPaymentBreakdown.Row = ({
  name,
  amount,
  content,
  className,
}: {
  name: any;
  amount?: number;
  content?: ReactElement;
  className?: string;
}) => {
  const { formatCurrency } = useAppMethods();

  return (
    <div className={classnames('flex justify-between gap-lg', className)}>
      <span className="flex items-center text-muted-foreground">{name}</span>
      <span className="">{content ?? formatCurrency(amount)}</span>
    </div>
  );
};
